import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { specificationsApi } from '@/api';
import { queryKeys } from '@/api/query-keys';
import {
  BulkApproveRequest,
  BulkEditItemSpecsRequest,
  BulkMergeRequest,
  EditItemSpecsRequest,
  ExtractRequest,
  ImportCyberportRequest,
  MergeRequest,
  ResourceType,
  ReviewQueueFilters,
  SpecState,
} from './specifications.types';

export const useReviewQueue = (
  filters: ReviewQueueFilters,
  options?: { refetchInterval?: number | false },
) =>
  useQuery({
    queryKey: queryKeys.specifications.queue(filters),
    queryFn: () => specificationsApi.reviewQueue(filters),
    placeholderData: keepPreviousData,
    refetchInterval: options?.refetchInterval,
  });

export const useExtractionJobs = (status?: string) =>
  useQuery({
    queryKey: queryKeys.specifications.extractionJobs(status),
    queryFn: () => specificationsApi.extractionJobs(status),
    refetchInterval: 5000,
  });

export const usePickerProductLines = (params: {
  manufacturerId?: number;
  category?: string;
  state?: SpecState;
  search?: string;
}) =>
  useQuery({
    queryKey: queryKeys.specifications.pickerProductLines(params),
    queryFn: () => specificationsApi.pickerProductLines(params),
    enabled: params.manufacturerId != null,
  });

export const usePickerProductModels = (params: {
  productLineId?: number;
  state?: SpecState;
  search?: string;
}) =>
  useQuery({
    queryKey: queryKeys.specifications.pickerModels(params),
    queryFn: () => specificationsApi.pickerProductModels(params),
    enabled: params.productLineId != null,
  });

export const usePickerVocabulary = (params: {
  manufacturerId?: number;
  attribute?: string;
  state?: SpecState;
  search?: string;
}) =>
  useQuery({
    queryKey: queryKeys.specifications.pickerVocab(params),
    queryFn: () => specificationsApi.pickerVocabulary(params),
    enabled: params.manufacturerId != null && params.attribute != null,
  });

const useSpecificationsInvalidation = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.specifications.all });
};

export const useExtract = () => {
  const invalidate = useSpecificationsInvalidation();
  return useMutation({
    mutationFn: (body: ExtractRequest) => specificationsApi.extract(body),
    onSuccess: invalidate,
  });
};

export const useApprove = () => {
  const invalidate = useSpecificationsInvalidation();
  return useMutation({
    mutationFn: (params: { resource: ResourceType; id: number }) =>
      specificationsApi.approve(params.resource, params.id),
    onSuccess: invalidate,
  });
};

export const useMerge = () => {
  const invalidate = useSpecificationsInvalidation();
  return useMutation({
    mutationFn: (params: {
      resource: ResourceType;
      id: number;
      body: MergeRequest;
    }) => specificationsApi.merge(params.resource, params.id, params.body),
    onSuccess: (_, vars) => {
      // Dry runs don't mutate state; skip invalidation.
      if (!vars.body.dryRun) invalidate();
    },
  });
};

export const useReject = () => {
  const invalidate = useSpecificationsInvalidation();
  return useMutation({
    mutationFn: (params: { resource: ResourceType; id: number }) =>
      specificationsApi.reject(params.resource, params.id),
    onSuccess: invalidate,
  });
};

export const useApproveBranch = () => {
  const invalidate = useSpecificationsInvalidation();
  return useMutation({
    mutationFn: (productLineId: number) =>
      specificationsApi.approveBranch(productLineId),
    onSuccess: invalidate,
  });
};

export const useBulkApprove = () => {
  const invalidate = useSpecificationsInvalidation();
  return useMutation({
    mutationFn: (body: BulkApproveRequest) =>
      specificationsApi.bulkApprove(body),
    onSuccess: invalidate,
  });
};

export const useBulkMerge = () => {
  const invalidate = useSpecificationsInvalidation();
  return useMutation({
    mutationFn: (body: BulkMergeRequest) => specificationsApi.bulkMerge(body),
    onSuccess: (_, vars) => {
      if (!vars.dryRun) invalidate();
    },
  });
};

export const useEditItemSpecs = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { itemId: string; body: EditItemSpecsRequest }) =>
      specificationsApi.editItem(vars.itemId, vars.body),
    onSuccess: () => {
      // Items list + specifications queue both reflect the change.
      qc.invalidateQueries({ queryKey: queryKeys.specifications.all });
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['itemsWithAllPrices'] });
    },
  });
};

export const useBulkEditItemSpecs = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkEditItemSpecsRequest) =>
      specificationsApi.bulkEditItems(body),
    onSuccess: (_, vars) => {
      if (vars.dryRun) return;
      qc.invalidateQueries({ queryKey: queryKeys.specifications.all });
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['itemsWithAllPrices'] });
    },
  });
};

export const useImportCyberport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ImportCyberportRequest) =>
      specificationsApi.importCyberport(body),
    onSuccess: (_, vars) => {
      if (vars.dryRun) return;
      qc.invalidateQueries({ queryKey: queryKeys.specifications.all });
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['itemsWithAllPrices'] });
    },
  });
};
